import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { backupService, bucketService } from '../services/api';
import { MinioBucketReport, MinioBucket } from '../types';
import { ArrowPathIcon, ExclamationCircleIcon, CheckCircleIcon, ExclamationTriangleIcon, TrashIcon, UserIcon, ShieldCheckIcon, KeyIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useProgress } from '../hooks/useProgress';
import ProgressBar from '../components/ui/ProgressBar';

const MinioBucketReportPage: React.FC = () => {
  const [report, setReport] = useState<MinioBucketReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  
  // Hook de progression pour la génération de rapport
  const {
    progress,
    isLoading: progressLoading,
    error: progressError,
    startProgress,
    resetProgress
  } = useProgress('/backups/minio/report/start-progress', 1000);
  
  // États pour l'onglet Gestion
  const [activeTab, setActiveTab] = useState<'rapport' | 'gestion'>('rapport');
  const [managementData, setManagementData] = useState<any>({
    buckets: [],
    users: [],
    policies: [],
    serviceAccounts: []
  });
  const [managementLoading, setManagementLoading] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteItem, setDeleteItem] = useState<any>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  // Fonction pour charger le rapport
  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await backupService.getMinioBucketReport();
      setReport(data);
    } catch (err: any) {
      console.error('Erreur lors de la récupération du rapport Minio:', err);
      setError(err.response?.data?.message || 'Impossible de charger le rapport. Veuillez réessayer plus tard.');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour rafraîchir le rapport avec progression
  const refreshReportWithProgress = async () => {
    try {
      setError(null);
      resetProgress();
      
      console.log('Démarrage de la génération avec progression...');
      const taskId = await startProgress();
      
      if (!taskId) {
        throw new Error('Impossible de démarrer la génération du rapport');
      }
      
      console.log(`Tâche démarrée: ${taskId}`);
    } catch (err: any) {
      console.error('Erreur lors du démarrage de la génération:', err);
      setError(err.response?.data?.message || 'Impossible de démarrer la génération du rapport.');
    }
  };

  // Fonction pour rafraîchir le rapport (méthode rapide sans progression)
  const refreshReport = async () => {
    try {
      setRefreshing(true);
      setError(null);
      const data = await backupService.refreshMinioBucketReport();
      setReport(data);
    } catch (err: any) {
      console.error('Erreur lors du rafraîchissement du rapport Minio:', err);
      setError(err.response?.data?.message || 'Impossible de rafraîchir le rapport. Veuillez réessayer plus tard.');
    } finally {
      setRefreshing(false);
    }
  };

  // Ne plus charger automatiquement le rapport au démarrage pour éviter les timeouts
  // L'utilisateur devra utiliser le bouton "Générer avec progression"
  // useEffect(() => {
  //   loadReport();
  // }, []);

  // Recharger le rapport quand la progression est terminée
  useEffect(() => {
    if (progress?.phase === 'completed') {
      console.log('Progression terminée, rechargement du rapport...');
      setTimeout(() => {
        loadReport();
      }, 1000); // Petit délai pour s'assurer que le cache est mis à jour
    }
  }, [progress?.phase]);

  // Fonction pour charger les données de gestion (restaurée avec les nouvelles API MC)
  const loadManagementData = async () => {
    try {
      setManagementLoading(true);
      const [bucketsRes, usersRes, policiesRes, serviceAccountsRes] = await Promise.all([
        bucketService.listBuckets('name', false), // Version allégée sans statistiques
        bucketService.listUsers(),
        bucketService.listPolicies(),
        bucketService.listServiceAccounts()
      ]);
      
      setManagementData({
        buckets: bucketsRes.buckets || [],
        users: usersRes.users || [],
        policies: policiesRes.policies || [],
        serviceAccounts: serviceAccountsRes.serviceAccounts || []
      });
    } catch (err: any) {
      console.error('Erreur lors du chargement des données de gestion:', err);
      setError(err.response?.data?.message || 'Impossible de charger les données de gestion.');
    } finally {
      setManagementLoading(false);
    }
  };

  // Fonction pour ouvrir la modal de suppression
  const openDeleteModal = (item: any, type: 'bucket' | 'user' | 'policy' | 'serviceAccount') => {
    setDeleteItem({ ...item, type });
    setShowDeleteModal(true);
  };

  // Fonction pour supprimer un élément
  const handleDelete = async () => {
    if (!deleteItem) return;
    
    try {
      setDeleting(true);
      
      switch (deleteItem.type) {
        case 'bucket':
          await bucketService.deleteBucket(deleteItem.name);
          break;
        case 'user':
          await bucketService.deleteUser(deleteItem.username);
          break;
        case 'policy':
          await bucketService.deletePolicy(deleteItem.name);
          break;
        case 'serviceAccount':
          await bucketService.deleteServiceAccount(deleteItem.accessKey);
          break;
      }
      
      // Recharger les données
      await loadManagementData();
      setShowDeleteModal(false);
      setDeleteItem(null);
    } catch (err: any) {
      console.error('Erreur lors de la suppression:', err);
      setError(err.response?.data?.message || 'Erreur lors de la suppression.');
    } finally {
      setDeleting(false);
    }
  };

  // Formater une date
  const formatDate = (dateString: string | null) => {
    try {
      if (!dateString) return 'Inconnue';
      const date = new Date(dateString);
      return format(date, 'dd MMMM yyyy à HH:mm', { locale: fr });
    } catch (err) {
      return dateString || 'Inconnue';
    }
  };

  // Obtenir la classe de couleur en fonction du statut
  const getStatusColorClass = (status: string) => {
    switch (status) {
      case 'recent':
        return 'text-green-600 dark:text-green-400';
      case 'warning':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'danger':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Rapport Minio - Buckets et Sites
        </h1>
        <div className="flex space-x-3">
          <button
            onClick={refreshReportWithProgress}
            disabled={progressLoading || progress?.phase === 'processing'}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
          >
            <ArrowPathIcon className={`h-5 w-5 mr-2 ${progressLoading ? 'animate-spin' : ''}`} />
            Générer avec progression
          </button>
          <button
            onClick={refreshReport}
            disabled={refreshing || progress?.phase === 'processing'}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400"
          >
            <ArrowPathIcon className={`h-5 w-5 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Rafraîchir rapide
          </button>
        </div>
      </div>

      {/* Barre de progression */}
      {progress && (
        <div className="mb-6">
          <ProgressBar progress={progress} />
        </div>
      )}

      {/* Erreur de progression */}
      {progressError && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Erreur de progression
              </h3>
              <p className="mt-2 text-sm text-red-700">{progressError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Onglets */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('rapport')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'rapport'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            📊 Rapport
          </button>
          <button
            onClick={() => {
              setActiveTab('gestion');
              loadManagementData();
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'gestion'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            ⚙️ Gestion
          </button>
        </nav>
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
          <p className="font-bold">Erreur</p>
          <p>{error}</p>
        </div>
      )}

      {activeTab === 'rapport' && (
        <>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : report ? (
        <>
          {report.fromCache && (
            <div className="bg-blue-50 dark:bg-blue-900 border-l-4 border-blue-500 text-blue-700 dark:text-blue-200 p-4 mb-6">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                <p>Rapport chargé depuis le cache. Généré le {formatDate(report.generatedAt || report.timestamp)}</p>
              </div>
              <p className="mt-1 text-sm">Pour obtenir des données à jour, cliquez sur le bouton "Rafraîchir".</p>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Total buckets</h3>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{report.buckets.length}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Sites avec Minio</h3>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">{report.websitesWithMinio}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Buckets orphelins</h3>
                <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{report.bucketsWithoutWebsite.length}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Sites sans bucket</h3>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400">{report.websitesWithoutBucket.length}</p>
              </div>
            </div>
          </div>

          {/* Sites sans bucket */}
          {report.websitesWithoutBucket.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-2" />
                Sites configurés avec Minio mais sans bucket existant
              </h2>
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Client
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          URL
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Bucket configuré
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {report.websitesWithoutBucket.map((site) => (
                        <tr key={site.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{site.clientName}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <a 
                              href={site.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {site.url}
                            </a>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-red-600 dark:text-red-400 font-mono">{site.bucketName}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <Link 
                              to={`/sites/${site.id}/edit`}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                            >
                              Modifier
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Buckets orphelins */}
          {report.bucketsWithoutWebsite.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 mr-2" />
                Buckets sans site web associé
              </h2>
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Nom du bucket
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Dernière modification
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Nombre d'objets
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Taille
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Statut
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {report.bucketsWithoutWebsite.map((bucket) => (
                        <tr key={bucket.name} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900 dark:text-white font-mono">{bucket.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500 dark:text-gray-300">
                              {formatDate(bucket.latestModification)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500 dark:text-gray-300">{bucket.objectCount}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500 dark:text-gray-300">{bucket.size}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm ${getStatusColorClass(bucket.status.code)}`}>
                              {bucket.status.message}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Liste de tous les buckets */}
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4">Tous les buckets ({report.buckets.length})</h2>
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Nom du bucket
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Dernière modification
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Nombre d'objets
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Taille
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Statut
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {report.buckets.map((bucket) => (
                      <tr key={bucket.name} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white font-mono">{bucket.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500 dark:text-gray-300">
                            {formatDate(bucket.latestModification)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500 dark:text-gray-300">{bucket.objectCount}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500 dark:text-gray-300">{bucket.size}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm ${getStatusColorClass(bucket.status.code)}`}>
                            {bucket.status.message}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-yellow-50 dark:bg-yellow-900 border-l-4 border-yellow-500 text-yellow-700 dark:text-yellow-200 p-4">
          <div className="flex items-center">
            <ExclamationCircleIcon className="h-6 w-6 mr-2" />
            <p>Aucun rapport disponible.</p>
          </div>
        </div>
      )}
        </>
      )}

      {/* Onglet Gestion */}
      {activeTab === 'gestion' && (
        <div>
          {managementLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Buckets */}
              <div>
                <h2 className="text-xl font-bold mb-4 flex items-center">
                  <ShieldCheckIcon className="h-6 w-6 text-blue-500 mr-2" />
                  Buckets ({managementData.buckets.length})
                </h2>
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Nom du bucket
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Date de création
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {managementData.buckets.map((bucket: any) => (
                          <tr key={bucket.name} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 dark:text-white font-mono">{bucket.name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500 dark:text-gray-300">
                                {formatDate(bucket.creationDate)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <button
                                onClick={() => openDeleteModal(bucket, 'bucket')}
                                className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                                title="Supprimer le bucket"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Utilisateurs */}
              <div>
                <h2 className="text-xl font-bold mb-4 flex items-center">
                  <UserIcon className="h-6 w-6 text-green-500 mr-2" />
                  Utilisateurs ({managementData.users.length})
                </h2>
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Nom d'utilisateur
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Statut
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Politique
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {managementData.users.map((user: any) => (
                          <tr key={user.username} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">{user.username}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                user.status === 'enabled' 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}>
                                {user.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500 dark:text-gray-300 font-mono">{user.policyName || 'Aucune'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <button
                                onClick={() => openDeleteModal(user, 'user')}
                                className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                                title="Supprimer l'utilisateur"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Politiques */}
              <div>
                <h2 className="text-xl font-bold mb-4 flex items-center">
                  <ShieldCheckIcon className="h-6 w-6 text-purple-500 mr-2" />
                  Politiques ({managementData.policies.length})
                </h2>
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Nom de la politique
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Bucket associé
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Description
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {managementData.policies.map((policy: any) => (
                          <tr key={policy.name} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">{policy.name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500 dark:text-gray-300 font-mono">{policy.bucketName}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500 dark:text-gray-300">{policy.description}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <button
                                onClick={() => openDeleteModal(policy, 'policy')}
                                className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                                title="Supprimer la politique"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Service Accounts */}
              <div>
                <h2 className="text-xl font-bold mb-4 flex items-center">
                  <KeyIcon className="h-6 w-6 text-orange-500 mr-2" />
                  Service Accounts ({managementData.serviceAccounts.length})
                </h2>
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Clé d'accès
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Bucket associé
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Politique
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Statut
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {managementData.serviceAccounts.map((sa: any) => (
                          <tr key={sa.accessKey} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 dark:text-white font-mono">{sa.accessKey}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500 dark:text-gray-300 font-mono">{sa.bucketName}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500 dark:text-gray-300 font-mono">{sa.policy}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                sa.status === 'on' 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}>
                                {sa.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <button
                                onClick={() => openDeleteModal(sa, 'serviceAccount')}
                                className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                                title="Supprimer le Service Account"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de suppression */}
      {showDeleteModal && deleteItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Confirmer la suppression
              </h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                Êtes-vous vraiment sûr de vouloir supprimer {deleteItem.type === 'bucket' ? 'le bucket' : deleteItem.type === 'user' ? 'l\'utilisateur' : deleteItem.type === 'policy' ? 'la politique' : 'le Service Account'} ?
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                                  <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                    {deleteItem.type === 'bucket' && deleteItem.name}
                    {deleteItem.type === 'user' && deleteItem.username}
                    {deleteItem.type === 'policy' && deleteItem.name}
                    {deleteItem.type === 'serviceAccount' && deleteItem.accessKey}
                  </p>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                Cette action est irréversible et supprimera définitivement {deleteItem.type === 'bucket' ? 'le bucket et toutes ses données' : deleteItem.type === 'user' ? 'l\'utilisateur et ses accès' : deleteItem.type === 'policy' ? 'la politique' : 'le Service Account et ses accès'}.
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteItem(null);
                }}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                disabled={deleting}
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 dark:bg-red-700 text-white rounded-md hover:bg-red-700 dark:hover:bg-red-800 transition-colors disabled:bg-red-400 dark:disabled:bg-red-600"
                disabled={deleting}
              >
                {deleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MinioBucketReportPage; 